// src/components/ParticipantDetails.tsx
import React from 'react';
import { Participant } from '../data/eurovisionData';

interface ParticipantDetailsProps {
  participant: Participant | null;
  isOpen: boolean;
  onClose: () => void;
  onAddToRanking: () => void;
  onRemove?: (id: number) => void;
  isAlreadyInRanking: boolean;
}

export function ParticipantDetails({ 
  participant, 
  isOpen, 
  onClose, 
  onAddToRanking,
  onRemove,
  isAlreadyInRanking
}: ParticipantDetailsProps) {
  // Define handleClose inside the component where onClose is in scope
  const handleClose = () => {
    onClose();
    // Reset state after closing
    setTimeout(() => {
      // Any state resets would go here
    }, 300);
  };
  
  if (!isOpen || !participant) return null;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="relative bg-white w-full max-w-md md:max-w-2xl rounded-lg overflow-hidden max-h-[90vh] flex flex-col">
        {/* Purple header */}
        <div className="bg-white border-b text-black p-4 flex justify-between items-center">
          <h2 className="text-xl font-bold flex items-center">
            <div className="relative mr-2">
              <img 
                src={`${process.env.PUBLIC_URL}/flags/${participant.countryCode}.png`}
                alt={`${participant.country} flag`}
                className="w-8 h-8"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                  (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
                }}
              />
              {/* Fallback if image fails */}
              <div className="hidden w-8 h-8 rounded-full bg-red-600 flex items-center justify-center text-white text-xs font-bold border-2 border-white">
                {participant.countryCode}
              </div>
            </div>
            {participant.country}
          </h2>
          <button 
            onClick={handleClose} // Use handleClose instead of onClose
            className="text-black hover:text-gray-500"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Content */}
        <div className="p-4 overflow-y-auto flex-grow">
          <div className="flex items-center mb-4">
            <img 
              src={`${process.env.PUBLIC_URL}/${participant.image}`} 
              alt={participant.name} 
              className="w-20 h-20 object-cover rounded"
            />
            <div className="ml-3">
              <h3 className="font-bold">{participant.name}</h3>
              <div className="text-sm text-gray-600">{participant.songTitle}</div>
              <div className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded inline-flex items-center mt-1">
                <span className="mr-1">Odds to win:</span>
                <span className="font-bold">{participant.odds.toFixed(2)}</span>
                <img 
                    src={`${process.env.PUBLIC_URL}/img/bet365.png`}
                    alt="Sportsbook"
                    className="w-4 h-4 rounded ml-2"
                />
              </div>
            </div>
          </div>
          
          <div className="mb-4">
            <p className="text-gray-700">
              {participant.description}
            </p>
          </div>
          
          {/* Video embed */}
          {participant.videoId && (
            <div className="aspect-video mb-4 bg-black rounded overflow-hidden">
              <iframe 
                src={`https://www.youtube.com/embed/${participant.videoId}`}
                title={`${participant.country} - ${participant.songTitle}`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                allowFullScreen
                className="w-full h-full"
              ></iframe>
            </div>
          )}
        </div>
        
        {/* Bottom action button */}
        <div className="p-4 border-t">
          {isAlreadyInRanking && onRemove ? (
            <button
              onClick={() => {
                if (onRemove) onRemove(participant.id);
                handleClose(); // Use handleClose instead of onClose
              }}
              className="w-full py-3 bg-red-600 text-white rounded-md font-medium hover:bg-red-700"
            >
              Remove from Ranking
            </button>
          ) : (
            <button
              onClick={() => {
                onAddToRanking();
                handleClose(); // Use handleClose instead of onClose
              }}
              className="w-full py-3 bg-purple-600 text-white rounded-md font-medium hover:bg-purple-700"
            >
              Add to Ranking
            </button>
          )}
        </div>
      </div>
    </div>
  );
}